import React, { useState, useEffect } from 'react';
import { List, Icon, IconButton } from '@material-ui/core';
import Link from 'react-router-dom/Link';
import DocumentListItem from './DocumentListItem';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import { get, set, uniq, flatten, size, merge, orderBy } from 'global/lib/lodash';
import useStyles from './DocumentList.styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import useDebounce from 'global/hooks/useDebounce';
import { Loader, useScreen } from 'global/styles';
import withSearchBar from 'global/components/withSearchBar';
import { CenterContent, LeftSideBar, RightSideBar, GridContainer } from 'global/components/perform-grid';
import LibraryFilters from '../Filters/LibraryFilters';
import { stringSearch } from 'global/utils';
import clsx from 'clsx';
import { FILEADD, getIcon, VIDEOADD } from '../definitions';
import arrayToTree from 'array-to-tree';
import { PrintView } from 'global/components/print';
import DocumentContainer from '../DocumentContainer';
import { flattenAllChildren } from 'global/utils';
import { SelectLangEditBar } from 'global/components/lang';
import { FilterChips } from 'global/components/filterchips';

export const Includes = ['document', 'classification', 'classification.system', 'classification.assembly', 'classification.material', 'classification.material.parent'];

export const Transform = (da) => {
  if (Array.isArray(da)) {
    return da.map((d) => ({ ...d, document: { ...(d.document || {}), classification: d.classification || {} } }));
  } else {
    return da;
  }
};

const DocumentList = (props) => {
  const { isDocumentPath } = props;
  const [data, _setData] = useState([]);
  const [sideFilters, setSideFilters] = useState({});
  const [searchString, setSearchString] = useState('');
  const [selected, setSelected] = useState({});
  const [printItems, setPrintItems] = useState([]);
  const libid = get(props, 'match.params.library', get(props, 'libid', 0));
  const debouncedSearch = useDebounce(searchString, 800);
  const { isMobile } = useScreen();
  const classes = useStyles({ isMobile });

  const ed = useRetrieveData({ url: `/editions/${libid}` }, { build: ['editions'] });
  const edition = useRetrieveData(
    { url: `/edition-documents` },
    {
      useLangFilter: true,
      useEffect: true,
      build: ['editionDocuments'],
      params: { include: Includes.join(','), filter: { edition_id: libid } },
      transformData: Transform,
    }
  );

  const getNewList = () => {
    const classMap = {};
    const items = edition.data;
    items.map((cl) => {
      const id = get(cl, 'document.classification.id');
      if (id) {
        set(classMap, id, [...get(classMap, id, []), cl]);
      }
    });

    return classMap;
  };

  const getChecklists = (flat = false) => {
    const classMap = getNewList();

    return flat ? flatten(Object.keys(classMap).map((k) => classMap[k])) : Object.keys(classMap).map((k) => classMap[k]);
  };

  const setData = (da) => {
    const map = {};
    da.map((da) => {
      const sysid = get(da, '0.document.classification.id');
      set(map, sysid, orderBy([...get(map, sysid, []), da], '0.order'));
    });

    const flat = flatten(Object.keys(map).map((k) => map[k]));
    const sorted = orderBy(flat, '0.order');

    _setData(sorted);
  };

  const generateSidebar = (vals = {}) => {
    const obj = merge(
      {},
      {
        doctype: {
          type: 'checkbox',
          title: 'Document Type',
          canShow: (c) => {
            return Object.keys(c.data).length > 1;
          },
          data: {
            checklist: {
              name: 'Checklist',
              icon: getIcon('checklist'),
              filter: (cl) => cl.find((c) => get(c, 'documentableType') === 'checklist'),
            },
            step_by_step: {
              name: 'Step by Step',
              icon: getIcon('step_by_step'),
              filter: (cl) => cl.find((c) => get(c, 'documentableType') === 'step_by_step'),
            },
            sow: {
              name: 'Scope of Work',
              icon: getIcon('sow'),
              filter: (cl) => cl.scopeOfWorkId,
            },
            examples: {
              name: 'Examples',
              icon: getIcon('example'),
              filter: (cl) => cl.exampleId,
            },
            library_file: {
              name: 'Files',
              icon: getIcon('library_file'),
              filter: (cl) => cl.find((c) => get(c, 'documentableType') === FILEADD.documentableType),
            },
            library_video: {
              name: 'Videos',
              icon: getIcon('library_video'),
              filter: (cl) => cl.find((c) => get(c, 'documentableType') === VIDEOADD.documentableType),
            },
          },
        },
        system: {
          type: 'checkbox',
          title: 'System',
          filter: '0.document.classification.system.id',
          data: {},
        },
        material: {
          type: 'checkbox',
          title: 'Material',
          filter: ['0.document.classification.material.id', '0.document.classification.material.parentId'],
          data: {},
        },
      },
      vals
    );

    const d = getChecklists(true);

    const allMaterials = [];

    const selectedSystems = [];

    let datatypes = [];

    d.map((cl) => {
      const dtype = get(cl, 'documentableType');
      if (datatypes.indexOf(dtype) === -1) {
        datatypes.push(dtype);
      }

      const system = get(cl, 'document.classification.system');
      if (system) {
        const sel = ['system', 'data', system.id];
        const currentSystem = get(obj, ['system', 'data', system.id], { value: false });
        const isSel = get(currentSystem, 'value');
        if (isSel) {
          selectedSystems.push(system.id);
        }
        set(obj, sel, {
          ...system,
          ...currentSystem,
          classification: get(cl, 'document.classification'),
        });
      }
      const material = get(cl, 'document.classification.material');
      if (material) {
        allMaterials.push({ ...material, system });
        const parent = get(material, 'parent');
        if (parent) {
          allMaterials.push(parent);
        }
      }
    });

    Object.keys(obj.doctype.data).map((k) => {
      if (datatypes.indexOf(k) === -1) {
        delete obj.doctype.data[k];
      }
    });

    const tree = arrayToTree(uniq(allMaterials, 'id'), { parentProperty: 'parentId' }).filter((t) => !t.parentId && size(t.children));
    var matList = [];

    const hasInArray = (arr1, arr2) => {
      return arr1.find((a) => arr2.indexOf(a) > -1);
    };

    tree.map((mat) => {
      get(mat, 'children', []).map((material) => {
        const sel = ['material', 'data', material.id];
        const cur = get(obj, sel, { value: false });

        const allChilds = flattenAllChildren(material.children || [])
          .map((c) => get(c, 'system.id'))
          .filter((f) => f);
        const sysIds = [get(material, 'system.id'), ...allChilds];
        if (!!size(selectedSystems) && !hasInArray(selectedSystems, sysIds) && matList.indexOf(material.id) === -1) {
          delete obj['material']['data'][material.id];
          return;
        } else {
          matList.push(material.id);
        }

        set(obj, sel, {
          ...material,
          ...cur,
        });
      });
    });

    setSideFilters(obj);
  };

  const onFilterChange = (values) => {
    generateSidebar(values);
  };

  const onSearchChange = (str) => {
    setSearchString(str);
  };

  useEffect(() => {
    let somethingSelected = false;
    let options = [...getChecklists()];

    const filters = {
      system: [],
      material: [],
      doctype: [],
    };

    for (let k in sideFilters) {
      const sf = sideFilters[k];
      for (let kk in sf.data) {
        const d = sf.data[kk];
        if (d.value) {
          somethingSelected = true;
          if (sf.filter) {
            filters[k].push({ filter: sf.filter, value: d.id, type: k, children: flattenAllChildren(d.children || []) });
          }
          if (d.filter) {
            filters[k].push(d.filter);
          }
        }
      }
    }

    const optPassesFilt = (opt, filt) => {
      const ff = filt.filter;
      const val = filt.value;
      if (Array.isArray(ff)) {
        return ff.filter((f) => get(opt, f) == val).length > 0;
      } else {
        return get(opt, ff) == val;
      }
    };

    const passesAnyChildren = (opt, filt) => {
      const res = optPassesFilt(opt, filt);
      if (res) {
        return res;
      }
      if (filt.children) {
        const childres = filt.children.filter((child) => {
          return optPassesFilt(child, filt);
        });
        return childres.length > 0;
      } else {
        return false;
      }
    };

    if (somethingSelected) {
      const totalFilterSize = Object.keys(filters)
        .map((k) => size(filters[k]))
        .reduce((a, item) => a + item, 0);

      options = options.filter((opt) => {
        const passObj = {
          system: [],
          material: [],
          doctype: [],
        };

        Object.keys(filters).map((k) => {
          const filts = get(filters, k);

          const passes = filts.filter((f) => {
            const hasFilter = get(f, 'filter');
            if (hasFilter) {
              return passesAnyChildren(opt, f);
            } else if (typeof f === 'function') {
              return f(opt);
            }
          });

          passObj[k] = [...passObj[k], ...passes];
        });

        const totalPass = Object.keys(passObj)
          .map((k) => size(passObj[k]))
          .reduce((a, item) => a + item, 0);

        const hasFromEachCategory = () => {
          const catsNeeded = Object.keys(filters)
            .filter((k) => size(filters[k]))
            .map((k) => k);
          const catsHave = Object.keys(passObj)
            .filter((k) => size(passObj[k]))
            .map((k) => k);
          let passesDoc = true;

          if (catsNeeded.indexOf('doctype') > -1) {
            const docPassNeed = size(get(filters, 'doctype'));
            const docpass = size(get(passObj, 'doctype'));
            passesDoc = docpass >= docPassNeed;
          }

          return passesDoc && size(catsHave.filter((f) => catsNeeded.indexOf(f) > -1)) === size(catsNeeded);
        };

        return totalPass >= totalFilterSize || hasFromEachCategory();
      });
    }

    if (searchString) {
      options = options.filter((opts) => !!size(stringSearch(searchString, opts, ['document.name', 'document.classification.name', 'document.classification.system.name'])));
    }
    setData(options);
  }, [sideFilters, debouncedSearch]);

  useEffect(() => {
    setData(getChecklists());
    generateSidebar(sideFilters);
  }, [edition.data]);

  useEffect(() => {
    window.DOC_LIST_EDITION = edition;

    return () => {
      delete window.DOC_LIST_EDITION;
    };
  }, []);

  const getSideFilterKeys = () => {
    const dfilters = get(sideFilters, 'doctype.data', {});
    return Object.keys(dfilters)
      .filter((k) => get(dfilters, [k, 'value']))
      .map((k) => k);
  };

  useEffect(() => {
    setPrintItems(
      data
        .filter((f) => get(selected, get(f, '0.document.classification.id')))
        .map((item) => {
          const className = get(item, '0.document.classification.system.name');
          return {
            coverTitle: `${className}: ${get(item, '0.document.name')}`,
            render: () => {
              const docFilters = getSideFilterKeys();
              return item
                .filter((it) => {
                  if (size(docFilters)) {
                    return docFilters.indexOf(it.documentableType) > -1;
                  } else {
                    return true;
                  }
                })
                .map((doc) => <DocumentContainer subText={get(doc, 'document.classification.system.name')} printView={true} doc={doc} showLogo={true} />);
            },
          };
        })
    );
  }, [selected]);

  const onSelect = (item) => {
    if (props.isModal) {
      return;
    }
    const sel = { ...selected };
    if (get(sel, item.id)) {
      delete sel[item.id];
    } else {
      set(sel, item.id, item);
    }
    setSelected(sel);
  };

  const addAllSelected = () => {
    const sel = {};
    data.map((d) => set(sel, get(d, '0.document.classification.id'), d));
    setSelected(sel);
  };

  const filterChipDel = (filt) => {
    const { pid, id } = filt;
    const conf = { ...sideFilters };
    const location = [pid, 'data', id, 'value'];
    const currentValue = get(conf, location);
    set(conf, location, !currentValue);
    onFilterChange(conf);
  };

  const getActiveFilters = () => {
    const active = [];
    for (let k in sideFilters) {
      const sf = sideFilters[k];
      Object.keys(sf.data).map((kk) => {
        const obj = get(sf, ['data', kk], {});
        const { value } = obj;
        if (!!value) {
          active.push({ ...obj, pid: k, id: obj.id || kk });
        }
      });
    }
    return active;
  };

  const renderFilterChips = () => {
    return <FilterChips filters={getActiveFilters()} onDelete={filterChipDel} />;
  };

  const showTrans = !isDocumentPath && (edition.data || []).find((d) => size(get(d, 'document.translations')) > 1);

  return (
    <GridContainer fullWidth={isDocumentPath}>
      <LeftSideBar hidden={isMobile || isDocumentPath}>
        <LibraryFilters config={sideFilters} onChange={onFilterChange} />
      </LeftSideBar>

      <CenterContent fullWidth={isDocumentPath}>
        <div className={isDocumentPath && clsx(classes.sidebarWidth, classes.list)}>
          <div className={classes.row}>
            <IconButton classes={{ root: classes.closeIcon }} component={Link} to={`/library`} {...(props.closeProps || {})}>
              <Icon>close</Icon>
            </IconButton>
            <h2>{edition.loading ? <Loader size={30} /> : get(ed, 'data.0.name')}</h2>
          </div>

          {isMobile ? <LibraryFilters config={sideFilters} onChange={onFilterChange} renderFilterChips={renderFilterChips} /> : renderFilterChips()}

          <ListWithSearch
            {...props}
            onSearchChange={onSearchChange}
            filterString={searchString}
            loading={edition.loading}
            data={data}
            alwaysReplaceData={true}
            selected={selected}
            sideFilters={sideFilters}
            getSideFilterKeys={getSideFilterKeys}
            onSelect={onSelect}
            header={!isDocumentPath && <SelectLangEditBar showTranslations={showTrans} canEdit={get(ed, 'data.0.userCanEdit', false)} />}
          />
        </div>

        <PrintView
          title={`Print Contents: ${size(printItems)} Standards`}
          items={printItems}
          onClose={() => setSelected({})}
          button={{
            title: 'Print All',
            onClick: addAllSelected,
          }}
        />
      </CenterContent>

      <RightSideBar />
    </GridContainer>
  );
};

const ListWithSearch = withSearchBar(withInfiniteScroll(DocumentListItem, List));

export default DocumentList;
